import React, { useState, useEffect, useMemo, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateSelectionForm from "../../../../shared/components/DateSelectionForm";
import BackAnchor from "../../../../shared/components/BackAnchor";
import BackNextButtons from "../../../../shared/components/BackNextButtons";
import { getApplicationStatus } from "../../../../redux/reducers/apiReducers/getServicesReducer";
import { getApplicantsAge } from "../../../../helpers/utils";

const CheckStatusView = ({ t, i18n }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState({
    dateOfBirth: null,
    confirmationNumber: null
  });
  const [dobFlag, setDobFlag] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [confirmationNumber, setConfirmationNumber] = useState(null);
  const [confirmationNumberValid, setConfirmationNumberValid] = useState(null);

  useEffect(() => {
    if (i18n?.language === "en") {
      document.title = "Check Application Status - HARP - Government of Nova Scotia";
    } else {
      document.title =
        "Vérifier l’état de la demande – PAC – Gouvernement de la Nouvelle-Écosse";
    }
  }, [i18n.language]);
  const handleConfirmationInputChange = (number) => {
    setConfirmationNumber(number);
    if (number) {
      const regex = RegExp(/^[0-9\b]+$/);
      const newPersonalErrors = { ...formErrors };
      if (!number || number?.length === 0 || number?.length !== 7) {
        newPersonalErrors.confirmationNumber =
          "errors.confirmation_number_empty";
        setConfirmationNumberValid("");
      } else {
        if (regex.test(number)) {
          newPersonalErrors.confirmationNumber = "";
          setConfirmationNumberValid(true);
        } else {
          newPersonalErrors.confirmationNumber =
            "errors.confirmation_number_invalid";
          setConfirmationNumberValid("");
        }
      }
      setFormErrors(newPersonalErrors);
    }
  };
  const [dobValidCharacter, setDobValidCharacter] = useState("");
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const datesData = useSelector((state) => state.datesReducer);
  let dates, currentLanguageDates;
  if (datesData) {
    dates = datesData["en"];
    currentLanguageDates = datesData[i18n.language];
  }
  const getDayAndMonth = (day, month, year) => {
    setDay(day);
    setMonth(month);
    setYear(year);
  };
  useEffect(() => {
    setFormErrors({ ...formErrors, dateOfBirth: false, ageError: false, dob: false })

  }, [day, month, year])


  const validateFields = useCallback((number, dob) => {
    const newPersonalErrors = { ...formErrors };
    const regex = RegExp(/^[0-9\b]+$/);
    // Sanitize input
    const sanitizedInput = number?.trim();
    const dateOfBirthError = !day || !month || !year;
    if (dateOfBirthError) {
      newPersonalErrors.dateOfBirth = dateOfBirthError
    }
    if (dob) {
      newPersonalErrors.ageError =
        getApplicantsAge(dateOfBirth, dates) < 18 || Number(year) < 1900 || Number(day) > 31 || (dobFlag ? true : false);
    }
    if (!dobValidCharacter) {
      newPersonalErrors.dob = true;
    }
    if (!number || number?.length === 0 || number?.length !== 7) {
      newPersonalErrors.confirmationNumber = "errors.confirmation_number_empty";
      setConfirmationNumberValid(false);
    } else {
      if (regex.test(sanitizedInput)) {
        newPersonalErrors.confirmationNumber = "";
        setConfirmationNumberValid(true);
      } else {
        newPersonalErrors.confirmationNumber =
          "errors.confirmation_number_invalid";
        setConfirmationNumberValid(false);
      }
    }
    setFormErrors(newPersonalErrors);
    const isValid = Object.values(newPersonalErrors).reduce(
      (prev, currentValue) => prev && !currentValue,
      true
    );

    const isFormClean = Object.values(newPersonalErrors).reduce(
      (prev, currentValue) => prev || currentValue === "",
      false
    );

    return isFormClean && isValid;
  }, [
    formErrors,
    dateOfBirth,
    dobValidCharacter,
    dobFlag,
    day,
    month,
    year,
    dates,

  ]);
  // useEffect(() => {
  //   document.title =
  //     "Check Application Status - HARP - Government of Nova Scotia";
  // }, []);

  const dobStatusClasses = useMemo(() => {
    if (formErrors.dateOfBirth === null && !dobFlag) {
      return "ns-form-group";
    } else if (formErrors.dateOfBirth) {
      return "ns-form-group has-error ns-form-group-message-error";
    }
  }, [formErrors, dobFlag]);

  const confirmationClasses = useMemo(() => {
    if (confirmationNumberValid) {
      return "ns-form-group";
    } else if (confirmationNumberValid === false) {
      return "ns-form-group has-error ns-form-group-message-error";
    }
  }, [confirmationNumberValid, confirmationNumber]);


  useEffect(() => {
    let monthNum = Number(month);
    let dayNum = Number(day);
    let yearNum = Number(year);
    if (!isNaN(dayNum) && !isNaN(yearNum)) {
      setDobValidCharacter(true);
    } else {
      setDobValidCharacter(false);
    }
    if (monthNum === 2 && dayNum === 29 && yearNum % 4 === 0) {
      setDobFlag(false);
    } else if (monthNum === 2 && dayNum > 28 && yearNum % 4 !== 0) {
      setDobFlag(true);
    } else if (monthNum === 4 && dayNum > 30) {
      setDobFlag(true);
    } else if (monthNum === 6 && dayNum > 30) {
      setDobFlag(true);
    } else if (monthNum === 9 && dayNum > 30) {
      setDobFlag(true);
    } else if (monthNum === 11 && dayNum > 30) {
      setDobFlag(true);
    } else {
      setDobFlag(false);
    }
  }, [day, month, year]);
  const onNext = () => {
    const dob = new Date(dateOfBirth)?.toISOString().split("T")[0];
    try {
      if (
        validateFields(confirmationNumber, dateOfBirth) &&
        confirmationNumber &&
        confirmationNumberValid
      ) {
        const confirmationData = {
          confirmationNumber: confirmationNumber,
          dob: dob
        };
        const statusCheckPromise = dispatch(
          getApplicationStatus(confirmationData)
        );
        setConfirmationNumberValid(true);
        statusCheckPromise.then((result) => {
          const statusResponse = result ? result?.amandaStatusCode : null;
          const currentStatus = result ? result?.currentStatus : null;
          const chequeStatus = currentStatus
            ? currentStatus?.applicationStatus
            : null;
          navigate(
            `/${i18n.language}${t(
              "routes.application_status"
            )}?response=${statusResponse}`,
            {
              state: {
                chequeStatus: chequeStatus,
                currentStatus: currentStatus
              }
            }
          );
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const backNextProps = {
    prevText: t("shared.back"),
    nextText: t("check_status.check_button"),
    nextOnClick: onNext
  };

  return (
    <div>
      <div className="container">
        <main>
          <article>
            <div className="row" style={{ paddingTop: "1em" }}></div>
            <BackAnchor backPath={"routes.start_page"} />
            <div className="row">
              <div className="col-xs-12">
                <h1 className="col-xs-12 col-sm-8" style={{ marginBottom: "30px" }}>
                  {t("check_status.title")}
                </h1>
              </div>
              <div className="col-xs-8" style={{ marginBottom: "20px" }}>
                <div className={confirmationClasses}>
                  {confirmationNumberValid === false && (
                    <div
                      id="street-address-error"
                      className="help-block-focused"
                    >
                      <i
                        aria-label={t("errors.hidden_error_msg")}
                        className="fa fa-exclamation-circle error-indicator errorAlert"
                        aria-hidden="true"
                      />
                      <span className="ns-visually-hidden">
                        {t("errors.hidden_error_msg")}
                      </span>
                      <span style={{ marginLeft: "10px" }}>
                        {t(formErrors.confirmationNumber)}
                      </span>
                    </div>
                  )}
                  <label className="col-xs-6">
                    {t("check_status.confirmation_number")}
                    <input
                      type="text"
                      pattern={"[0-9]*"}
                      inputMode="numeric"
                      maxLength={"7"}
                      onChange={(e) =>
                        handleConfirmationInputChange(e.target.value)
                      }
                    />
                  </label>
                </div>
              </div>
            </div>
          </article>
          <div style={{ paddingBottom: "1.5em" }} className="row">
            <div className={`${formErrors.dateOfBirth || formErrors.ageError || formErrors.dob ? 'ns-form-group has-error ns-form-group-message-error' : 'ns-form-group '}`}>
              <div className="col-xs-12 col-md-8">
                <strong> {t("check_status.dob")} </strong>
                <div style={{ marginBottom: ".5em" }} className="ns-hint">
                  {t("personal_information.date_of_birth_helper")}
                </div>
              </div>
              <div
                className="col-xs-12 col-md-8"
                style={{ marginBottom: ".5em" }}
              >
                {(formErrors.dateOfBirth || formErrors.ageError || formErrors.dob) && (
                  <div id="street-address-error" className="help-block-focused">
                    <i
                      aria-label={t("errors.hidden_error_msg")}
                      className="fa fa-exclamation-circle error-indicator errorAlert"
                      aria-hidden="true"
                    />
                    <span className="ns-visually-hidden">
                      {t("errors.hidden_error_msg")}
                    </span>
                    <span style={{ marginLeft: "10px" }}>
                      {formErrors.dateOfBirth
                        ? t("personal_information.date_of_birth_error")
                        : formErrors.ageError
                          ? t("personal_information.age_error")
                          : t("personal_information.date_error")}
                    </span>
                  </div>
                )}
                <DateSelectionForm
                  id={"personal-information-date"}
                  date={dateOfBirth}
                  language={i18n.language}
                  getDayAndMonth={getDayAndMonth}
                  onDateChange={(date) => {
                    setDateOfBirth(date);
                    const newPersonalErrors = { ...formErrors };
                    newPersonalErrors.dateOfBirth = date === 0 ? null : false;
                    setFormErrors(newPersonalErrors);
                  }}
                />
              </div>
            </div>
          </div>
          <BackNextButtons
            props={backNextProps}
            displayBack={false}
            nextOnClick={onNext}
            isGreenButton={true}
          />
        </main>
      </div>
    </div>
  );
};

export default withTranslation("common")(CheckStatusView);
